// Competitive Mode: Main page integrating leaderboards, tournaments, rankings, and battles
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Trophy, Zap, Swords, Calendar, TrendingUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  UserRankingCard,
  Leaderboard,
  TournamentsList,
  type UserRanking,
} from "../components/CompetitiveMode";

export default function Competitive() {
  const navigate = useNavigate();
  const [userRanking, setUserRanking] = useState<UserRanking | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [canCreateTournaments, setCanCreateTournaments] = useState(false);

  useEffect(() => {
    fetchUserRanking();
    checkTournamentPermissions();
  }, []);

  const checkTournamentPermissions = async () => {
    const { data, error } = await supabase.rpc("can_create_tournaments");
    if (!error && data) {
      setCanCreateTournaments(data);
    }
  };

  const fetchUserRanking = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("user_rankings")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!error && data) {
      setUserRanking(data);
    } else if (!data) {
      // Initialize user ranking if doesn't exist
      const { data: newRanking, error: insertError } = await supabase
        .from("user_rankings")
        .insert({
          user_id: user.id,
          rating: 1000,
          total_score: 0,
          competitions_entered: 0,
          competitions_won: 0,
          win_rate: 0,
          highest_rating: 1000,
          rank_tier: "bronze",
        })
        .select()
        .single();

      if (!insertError && newRanking) {
        setUserRanking(newRanking);
      }
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          Loading competitive mode...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8 text-yellow-500" />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              Tournament Hall
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Face warriors worldwide, climb the ranks, and prove your mastery!
          </p>
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="leaderboards"
              className="flex items-center gap-2"
            >
              <Trophy className="w-4 h-4" />
              Leaderboards
            </TabsTrigger>
            <TabsTrigger
              value="tournaments"
              className="flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Tournaments
            </TabsTrigger>
            <TabsTrigger value="battles" className="flex items-center gap-2">
              <Swords className="w-4 h-4" />
              Battles
            </TabsTrigger>
          </TabsList>

          {/* Tab Content */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* User Ranking Card */}
              <UserRankingCard ranking={userRanking} />

              {/* Quick Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Competitions Won
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {userRanking?.competitions_won || 0}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Win Rate
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {userRanking?.win_rate?.toFixed(1) || 0}%
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Peak Rating
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {userRanking?.highest_rating || 1000}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Weekly Leaderboard Preview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Weekly Leaderboard
                  </h2>
                  <button
                    onClick={() => setActiveTab("leaderboards")}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
                  >
                    View All →
                  </button>
                </div>
                <Leaderboard />
              </div>

              {/* Active Tournaments Preview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Active Tournaments
                  </h2>
                  <button
                    onClick={() => setActiveTab("tournaments")}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
                  >
                    View All →
                  </button>
                </div>
                <TournamentsList />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="leaderboards">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Global Leaderboards
              </h2>
              <Leaderboard />
            </div>
          </TabsContent>

          <TabsContent value="tournaments">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Tournaments
                </h2>
                {canCreateTournaments && (
                  <Button
                    onClick={() => navigate("/tournament-create")}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create Tournament
                  </Button>
                )}
              </div>
              <TournamentsList />
            </div>
          </TabsContent>

          <TabsContent value="battles">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center shadow-sm">
              <Swords className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Battles Coming Soon!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Head-to-head battles are currently in development. Challenge
                other players to real-time math showdowns!
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-700 dark:text-blue-300">
                <Zap className="w-5 h-5" />
                <span className="font-medium">Coming in the next update</span>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
